import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type {
  IPaginatedDrepDelegationsResponse,
  IPaginatedDrepDelegatorsResponse,
  IPaginatedDRepRegistrationsResponse,
  IPaginatedDRepVotesResopnse,
} from '@src/store/drep/types';
import {
  type DRepMetadata,
  type IDelegateDRepResponse,
  type IDRepStats,
  type ILoginRequestBody,
  type ILogoutResponse,
  type IMintDRepTokenRequestBody,
  type IMintDRepTokenResponse,
  type ISubmitTxRequestBody,
  type IMintedDrepMetaData,
} from '@src/store/drep/types';
import environments from '@src/configs/environments';
import { bech32ToHex } from '@src/utils/dRepUtils';

export const DREP_PATH = 'drep';

type IParams = {
  page: number;
  size: number;
};

export const drepApi = createApi({
  reducerPath: DREP_PATH,
  refetchOnReconnect: true,
  refetchOnMountOrArgChange: true,
  keepUnusedDataFor: 0,
  baseQuery: fetchBaseQuery({
    baseUrl: environments.BASE_API_URL,
    credentials: 'include',
    prepareHeaders: (headers) => {
      headers.append('Content-Type', 'application/json');
      return headers;
    },
  }),
  endpoints: (builder) => ({
    getDRepById: builder.query<DRepMetadata, string>({
      query: (id) => ({
        url: `/drep?id=${id}`,
        method: 'GET',
      }),
    }),
    getDRepByName: builder.query<DRepMetadata, string>({
      query: (name) => ({
        url: `/drep?name=${name}`,
        method: 'GET',
      }),
    }),
    mintDRepToken: builder.mutation<
      IMintDRepTokenResponse,
      IMintDRepTokenRequestBody
    >({
      query: (mintDRepTokenRequestBody) => ({
        url: `/register`,
        method: 'POST',
        body: JSON.stringify(mintDRepTokenRequestBody),
      }),
    }),
    getDelegatedDRep: builder.query<IDelegateDRepResponse | null, string>({
      query: (stakeAddress) => ({
        url: `/vote/delegation?stakeAddress=${stakeAddress}`,
        method: 'GET',
      }),
    }),
    submitDRepToken: builder.mutation<
      IMintDRepTokenResponse,
      ISubmitTxRequestBody
    >({
      query: (submitDrepTokenRequestBody) => ({
        url: `/submit`,
        method: 'POST',
        body: JSON.stringify(submitDrepTokenRequestBody),
      }),
    }),
    dRepUnique: builder.query<boolean, string>({
      query: (name) => ({
        url: `/is-unique?name=${name}`,
        method: 'GET',
      }),
    }),
    dRepLogin: builder.mutation<boolean, ILoginRequestBody>({
      query: (loginRequestBody) => ({
        url: `/login`,
        method: 'POST',
        body: JSON.stringify(loginRequestBody),
      }),
    }),
    dRepLogout: builder.mutation<ILogoutResponse, void>({
      query: () => ({
        url: `/logout`,
        method: 'GET',
      }),
    }),
    getDRepVotes: builder.query<
      IPaginatedDRepVotesResopnse,
      { dRepId: string; params: IParams }
    >({
      query: ({ dRepId, params }) => ({
        url: `/drep/${bech32ToHex(dRepId)}/vote?page=${params.page}&size=${params.size}`,
        method: 'GET',
      }),
    }),
    getActiveDRepDelegators: builder.query<
      IPaginatedDrepDelegatorsResponse,
      { dRepId: string; params: IParams }
    >({
      query: ({ dRepId, params }) => ({
        url: `/drep/${bech32ToHex(dRepId)}/delegator?page=${params.page}&size=${params.size}`,
        method: 'GET',
      }),
    }),
    getDRepRegistrations: builder.query<
      IPaginatedDRepRegistrationsResponse,
      { dRepId: string; params: IParams }
    >({
      query: ({ dRepId, params }) => ({
        url: `/drep/${bech32ToHex(dRepId)}/registration?page=${params.page}&size=${params.size}`,
        method: 'GET',
      }),
    }),
    getDRepStats: builder.query<IDRepStats, string>({
      query: (dRepId) => ({
        url: `/drep/stats?id=${dRepId}`,
        method: 'GET',
      }),
    }),
    getMintedDRepMetadata: builder.query<
      IMintedDrepMetaData[],
      number | undefined
    >({
      query: (limit = 10) => ({
        url: `/drep/accounts?limit=${limit}`,
        method: 'GET',
      }),
    }),
    getDRepDelegations: builder.query<
      IPaginatedDrepDelegationsResponse,
      { dRepId: string; params: IParams }
    >({
      query: ({ dRepId, params }) => ({
        url: `/drep/${dRepId}/delegation?page=${params.page}&size=${params.size}`,
        method: 'GET',
      }),
    }),
  }),
});

export const {
  useGetDRepByIdQuery,
  useGetDRepByNameQuery,
  useLazyGetDRepByNameQuery,
  useLazyGetDRepByIdQuery,
  useMintDRepTokenMutation,
  useGetDelegatedDRepQuery,
  useLazyGetDelegatedDRepQuery,
  useSubmitDRepTokenMutation,
  useDRepLoginMutation,
  useDRepLogoutMutation,
  useLazyDRepUniqueQuery,
  useLazyGetDRepVotesQuery,
  useGetDRepVotesQuery,
  useGetActiveDRepDelegatorsQuery,
  useGetDRepRegistrationsQuery,
  useGetDRepStatsQuery,
  useGetMintedDRepMetadataQuery,
  useGetDRepDelegationsQuery,
} = drepApi;
